// redux/slices/clientPackagesSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getJwtToken } from "../../Utils/authStorage";
import { getServerUrl } from "../../Utils/serverSelector";
import { resetStore } from "./resetStore";

/* ===================== HELPERS ===================== */

const axiosRequest = async ({
  url,
  method = "GET",
  headers = {},
  data = null,
}) => {
  const jwt = await getJwtToken();

  try {
    const response = await axios({
      url: `${getServerUrl()}${url}`,
      method,
      headers: {
        "Content-Type": "application/json",
        ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        ...headers,
      },
      data,
    });

    return response.data;
  } catch (error) {
    throw {
      status: error?.response?.status,
      data: error?.response?.data,
      message:
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Request failed",
    };
  }
};

const getErrorMessage = (error, fallback) =>
  error?.data?.message ||
  error?.data?.error ||
  error?.message ||
  fallback;

/* ===================== THUNKS ===================== */

export const fetchClientPackages = createAsyncThunk(
  "clientPackages/fetchClientPackages",
  async ({ clientId }, { rejectWithValue }) => {
    try {
      if (!clientId) throw new Error("clientId не передан");

      const data = await axiosRequest({
        url: `/client-packages/client/${clientId}`,
      });

      return Array.isArray(data) ? data : [];
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Fetch Client Packages failed")
      );
    }
  }
);

export const fetchMasterClientPackages = createAsyncThunk(
  "clientPackages/fetchMasterClientPackages",
  async ({ uid, status }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const url = status
        ? `/client-packages/master/${uid}?status=${encodeURIComponent(status)}`
        : `/client-packages/master/${uid}`;

      const data = await axiosRequest({ url });

      return Array.isArray(data) ? data : [];
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Fetch Master Client Packages failed")
      );
    }
  }
);

export const fetchClientPackageById = createAsyncThunk(
  "clientPackages/fetchClientPackageById",
  async ({ id }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID не передан");

      const data = await axiosRequest({
        url: `/client-packages/${id}`,
      });

      return data || null;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Fetch Client Package failed")
      );
    }
  }
);

export const assignPackageToClient = createAsyncThunk(
  "clientPackages/assignPackageToClient",
  async (packageData, { rejectWithValue }) => {
    try {
      const data = await axiosRequest({
        url: "/client-packages/assign",
        method: "POST",
        data: packageData,
      });

      return data?.clientPackage || data || null;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Assign Package failed"));
    }
  }
);

export const returnPackageSession = createAsyncThunk(
  "clientPackages/returnPackageSession",
  async ({ id, date, time }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID не передан");

      const data = await axiosRequest({
        url: `/client-packages/${id}/return-session`,
        method: "POST",
        data: { date, time },
      });

      return data?.clientPackage || data || null;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Return Session failed"));
    }
  }
);

export const usePackageSession = createAsyncThunk(
  "clientPackages/usePackageSession",
  async ({ id, date, time, notes }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID не передан");

      const data = await axiosRequest({
        url: `/client-packages/${id}/use-session`,
        method: "POST",
        data: {
          notes,
          date: date || new Date().toISOString().split("T")[0],
          time: time || "00:00",
        },
      });

      return data?.clientPackage || data || null;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Use Session failed"));
    }
  }
);

export const updatePackageStatus = createAsyncThunk(
  "clientPackages/updatePackageStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID не передан");

      const data = await axiosRequest({
        url: `/client-packages/${id}/status`,
        method: "PATCH",
        data: { status },
      });

      return data?.clientPackage || data || null;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Update Package Status failed")
      );
    }
  }
);

export const updateParticipants = createAsyncThunk(
  "clientPackages/updateParticipants",
  async ({ id, maxParticipants, totalParticipants }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID не передан");

      const data = await axiosRequest({
        url: `/client-packages/${id}/participants`,
        method: "PATCH",
        data: {
          ...(maxParticipants !== undefined && { maxParticipants }),
          ...(totalParticipants !== undefined && { totalParticipants }),
        },
      });

      return data?.clientPackage || data || null;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Update Participants failed")
      );
    }
  }
);

export const deleteClientPackage = createAsyncThunk(
  "clientPackages/deleteClientPackage",
  async ({ id }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID не передан");

      await axiosRequest({
        url: `/client-packages/${id}`,
        method: "DELETE",
      });

      return id;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Delete Client Package failed")
      );
    }
  }
);

export const fetchClientPackageStats = createAsyncThunk(
  "clientPackages/fetchClientPackageStats",
  async ({ clientId }, { rejectWithValue }) => {
    try {
      if (!clientId) throw new Error("clientId не передан");

      const data = await axiosRequest({
        url: `/client-packages/client/${clientId}/stats`,
      });

      return data || null;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Fetch Client Package Stats failed")
      );
    }
  }
);

export const fetchCabinetClientPackages = createAsyncThunk(
  "clientPackages/fetchCabinetClientPackages",
  async ({ phone }, { rejectWithValue }) => {
    try {
      if (!phone) throw new Error("Phone не передан");

      const data = await axiosRequest({
        url: `/client-packages/cabinet?phone=${encodeURIComponent(phone)}`,
      });

      return Array.isArray(data?.packages) ? data.packages : [];
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to load client packages")
      );
    }
  }
);

/* ===================== HELPERS STATE ===================== */

const updatePackageInState = (state, updated) => {
  if (!updated?.id) return;

  const updateArray = (arr) => {
    const index = arr.findIndex(
      (item) => String(item?.id) === String(updated.id)
    );

    if (index !== -1) {
      arr[index] = updated;
    }
  };

  updateArray(state.clientPackages);
  updateArray(state.allClientPackages);

  if (String(state.currentPackage?.id) === String(updated.id)) {
    state.currentPackage = updated;
  }

  state.status = "succeeded";
};

/* ===================== STATE ===================== */

const initialState = {
  clientPackages: [],
  allClientPackages: [],
  currentPackage: null,
  stats: null,
  status: "idle",
  error: null,
};

/* ===================== SLICE ===================== */

const clientPackagesSlice = createSlice({
  name: "clientPackages",
  initialState,
  reducers: {
    clearCurrentPackage: (state) => {
      state.currentPackage = null;
    },
    clearClientPackages: (state) => {
      state.clientPackages = [];
      state.currentPackage = null;
      state.stats = null;
      state.status = "idle";
      state.error = null;
    },
    clearAllClientPackages: (state) => {
      state.allClientPackages = [];
    },
    clearClientPackagesError: (state) => {
      state.error = null;
    },
    resetClientPackagesStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientPackages.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchClientPackages.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.clientPackages = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchClientPackages.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Fetch Client Packages failed";
      })

      .addCase(fetchMasterClientPackages.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchMasterClientPackages.fulfilled, (state, action) => {
        state.status = "succeeded";
        const list = Array.isArray(action.payload) ? action.payload : [];
        state.allClientPackages = list;
        state.clientPackages = list;
      })
      .addCase(fetchMasterClientPackages.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Fetch Master Client Packages failed";
      })

      .addCase(fetchClientPackageById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchClientPackageById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.currentPackage = action.payload || null;
      })
      .addCase(fetchClientPackageById.rejected, (state, action) => {
        state.status = "failed";
        state.currentPackage = null;
        state.error =
          action.payload ||
          action.error?.message ||
          "Fetch Client Package failed";
      })

      .addCase(assignPackageToClient.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(assignPackageToClient.fulfilled, (state, action) => {
        state.status = "succeeded";
        const pkg = action.payload;
        if (!pkg) return;

        state.clientPackages.push(pkg);
        state.allClientPackages.push(pkg);
      })
      .addCase(assignPackageToClient.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Assign Package failed";
      })

      .addCase(returnPackageSession.fulfilled, (state, action) => {
        updatePackageInState(state, action.payload);
      })
      .addCase(returnPackageSession.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Return Session failed";
      })

      .addCase(usePackageSession.fulfilled, (state, action) => {
        updatePackageInState(state, action.payload);
      })
      .addCase(usePackageSession.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Use Session failed";
      })

      .addCase(updatePackageStatus.fulfilled, (state, action) => {
        updatePackageInState(state, action.payload);
      })
      .addCase(updatePackageStatus.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Update Package Status failed";
      })

      .addCase(updateParticipants.fulfilled, (state, action) => {
        updatePackageInState(state, action.payload);
      })
      .addCase(updateParticipants.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Update Participants failed";
      })

      .addCase(deleteClientPackage.fulfilled, (state, action) => {
        state.status = "succeeded";
        const id = String(action.payload);

        state.clientPackages = state.clientPackages.filter(
          (item) => String(item?.id) !== id
        );

        state.allClientPackages = state.allClientPackages.filter(
          (item) => String(item?.id) !== id
        );

        if (String(state.currentPackage?.id) === id) {
          state.currentPackage = null;
        }
      })
      .addCase(deleteClientPackage.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Delete Client Package failed";
      })

      .addCase(fetchClientPackageStats.fulfilled, (state, action) => {
        state.stats = action.payload || null;
      })
      .addCase(fetchClientPackageStats.rejected, (state, action) => {
        state.error =
          action.payload ||
          action.error?.message ||
          "Fetch Client Package Stats failed";
      })

      .addCase(fetchCabinetClientPackages.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchCabinetClientPackages.fulfilled,
        (state, action) => {
          state.loading = false
          state.clientPackages = action.payload
        }
      )
      .addCase(
        fetchCabinetClientPackages.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.payload
          state.clientPackages = []
        }
      )

      .addCase(resetStore, () => initialState);
  },
});

/* ===================== ACTIONS ===================== */

export const {
  clearCurrentPackage,
  clearClientPackages,
  clearAllClientPackages,
  clearClientPackagesError,
  resetClientPackagesStatus,
} = clientPackagesSlice.actions;

/* ===================== SELECTORS ===================== */

export const selectClientPackagesState = (state) => state.clientPackages;
export const selectClientPackages = (state) =>
  state.clientPackages?.clientPackages || [];
export const selectAllClientPackages = (state) =>
  state.clientPackages?.allClientPackages || [];
export const selectCurrentClientPackage = (state) =>
  state.clientPackages?.currentPackage || null;
export const selectClientPackageStats = (state) =>
  state.clientPackages?.stats || null;
export const selectClientPackagesStatus = (state) =>
  state.clientPackages?.status || "idle";
export const selectClientPackagesError = (state) =>
  state.clientPackages?.error || null;

export default clientPackagesSlice.reducer;